/**
 * SidePanel — animated side-drawer UI primitive.
 *
 * Props
 * -----
 * id        string          Unique identifier for this panel (used for ARIA + panelStack)
 * open      bool            Controlled open state
 * onClose   function        Called when the panel requests to be closed
 * title     string          Panel heading text
 * width     'sm'|'md'|'lg'  Drawer width: 380 / 480 / 640 px (default 'md')
 * side      'right'|'left'  Which edge the panel slides in from (default 'right')
 * stackable bool            When false, opening this panel closes all other non-stackable panels
 *                           (default false)
 * children  node            Panel body content
 *
 * Accessibility
 * -------------
 * - role="dialog" + aria-modal="true"
 * - aria-labelledby points to the title element
 * - Focus trap: Tab/Shift+Tab cycle through focusable elements inside the panel
 * - First focusable element receives focus when panel opens
 */

import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import usePanelStack from '../../store/panelStack';

// Width map
const WIDTH_MAP = {
  sm: 380,
  md: 480,
  lg: 640,
};

// Focusable element query used for the focus trap
const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function SidePanel({
  id,
  open,
  onClose,
  title,
  width = 'md',
  side = 'right',
  stackable = false,
  children,
}) {
  const theme = useTheme();
  const panelRef = useRef(null);
  const bodyRef = useRef(null);

  const panelWidth = WIDTH_MAP[width] ?? WIDTH_MAP.md;

  // Animation variants — tween with cubic-bezier for a natural slide feel (no bounce)
  const offscreen = side === 'right' ? '100%' : '-100%';
  const variants = {
    hidden: { x: offscreen },
    visible: {
      x: 0,
      transition: { type: 'tween', duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] },
    },
    exit: {
      x: offscreen,
      transition: { type: 'tween', duration: 0.22, ease: [0.55, 0, 1, 0.45] },
    },
  };

  // Sync with panelStack store
  useEffect(() => {
    if (open) {
      usePanelStack.getState().open(id, { stackable });
    } else {
      usePanelStack.getState().close(id);
    }
  }, [open, id, stackable]);

  // Focus trap + initial focus
  useEffect(() => {
    if (!open) return;

    const getFocusableElements = () => {
      if (!panelRef.current) return [];
      return Array.from(panelRef.current.querySelectorAll(FOCUSABLE_SELECTORS)).filter(
        (el) => !el.closest('[aria-hidden="true"]')
      );
    };

    // Focus first focusable element
    const raf = requestAnimationFrame(() => {
      const focusable = getFocusableElements();
      if (focusable.length > 0) {
        focusable[0].focus();
      } else if (panelRef.current) {
        panelRef.current.focus();
      }
    });

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        // Shift+Tab: if focus is on first element, wrap to last
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: if focus is on last element, wrap to first
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const panelPosition =
    side === 'right'
      ? { right: 0, left: 'auto' }
      : { left: 0, right: 'auto' };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key={`${id}-backdrop`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            onClick={handleBackdropClick}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: theme.zIndex.drawer,
              backgroundColor: theme.palette.common.black,
              opacity: 0.4,
            }}
          />

          {/* Panel */}
          <motion.div
            key={`${id}-panel`}
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${id}-title`}
            tabIndex={-1}
            variants={variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              position: 'fixed',
              top: 0,
              bottom: 0,
              width: panelWidth,
              zIndex: theme.zIndex.drawer + 1,
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[16],
              display: 'flex',
              flexDirection: 'column',
              outline: 'none',
              ...panelPosition,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 2,
                bgcolor: 'background.paper',
                borderBottom: `1px solid ${theme.palette.divider}`,
                zIndex: 1,
                flexShrink: 0,
              }}
            >
              <Typography
                id={`${id}-title`}
                variant="h6"
                component="h2"
                sx={{ fontWeight: 600 }}
              >
                {title}
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                aria-label="Close panel"
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Body */}
            <Box
              ref={bodyRef}
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 3,
              }}
            >
              {children}
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

SidePanel.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  width: PropTypes.oneOf(['sm', 'md', 'lg']),
  side: PropTypes.oneOf(['right', 'left']),
  stackable: PropTypes.bool,
  children: PropTypes.node,
};

export default SidePanel;
